import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { Box, Paper, Typography, CircularProgress, Grid } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { signInWithEmailLink, isSignInWithEmailLink } from 'firebase/auth';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorOutline, MailOutline } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
// Internal dependencies
import { ButtonSG } from '../components';
import { auth } from '../auth';
import { studioGlobal } from '../assets/logos';
import { UserContext } from '../auth';
import { styles } from './styles';
import { EmailForm } from './EmailForm';
import { EMAIL_LINK_STAGES, CLIENT_ENDPOINTS, LOG_LEVELS, SERVICES, ENDPOINTS, } from '../constants';
import { axiosClient } from '../axios';
/**
 * Email Link Verification
 * @param {any} props
 * @returns {JSX.Element}
 */
export const EmailLinkVerification = function (props) {
    // Sign in via Email Link stages
    const [stage, setStage] = useState(EMAIL_LINK_STAGES.VERIFYING);
    const { t } = useTranslation();
    // React router dom's state of the current element
    const { state } = useLocation();
    const navigate = useNavigate();
    // React router dom Search params
    const [searchParams] = useSearchParams();
    // get path from search params
    const path = searchParams.get('path');
    /**
     * Handle email submit for verification
     * @param {string} email
     * @returns {void}
     */
    const onSubmit = (email) => {
        try {
            signInWithEmailLink(auth, email, window.location.href)
                .then((result) => {
                window.localStorage.removeItem('emailForSignIn');
            })
                .catch((err) => {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: err.message,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.SIGN_IN,
                        userId: null,
                        email,
                        userAgent: navigator.userAgent,
                    },
                });
                setStage(EMAIL_LINK_STAGES.NOTIFY_ERROR);
            });
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: err.message,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.SIGN_IN,
                    userId: null,
                    email,
                    userAgent: navigator.userAgent,
                },
            });
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
    };
    // Sign In With Email Link listener
    useEffect(() => {
        if (isSignInWithEmailLink(auth, window.location.href)) {
            let email = window.localStorage.getItem('emailForSignIn');
            if (!email) {
                setStage(EMAIL_LINK_STAGES.REINPUT_EMAIL);
            }
            else {
                onSubmit(email);
            }
            ;
        }
    }, []);
    const user = useContext(UserContext);
    // Sign in listener
    useEffect(() => {
        if (user) {
            navigate(`${path}`);
        }
    }, [user]);
    // Title & subtitle
    const title = stage === EMAIL_LINK_STAGES.REINPUT_EMAIL ? 'Verify your email' : 'Verify your email';
    const subtitle = stage === EMAIL_LINK_STAGES.REINPUT_EMAIL ? 'Confirm your email address to complete log in' : 'The link is not valid. This can happen if the link is malformed, expired, or has already been used.';
    /** Handle reinput email submit */
    const handleSubmit = (email) => { onSubmit(email.toLowerCase()); };
    return (_jsx(Box
    // Email link verification container
    , Object.assign({ 
        // Email link verification container
        sx: styles.container }, { children: _jsxs(Box, Object.assign({ sx: {
                width: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.40)',
                height: '100%',
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                overflow: 'auto',
            } }, { children: [_jsxs(Box
                // Verifying block
                , Object.assign({ 
                    // Verifying block
                    sx: {
                        display: stage === EMAIL_LINK_STAGES.VERIFYING ? 'block' : 'none',
                        position: 'absolute',
                        top: '50vh',
                        left: '50vw',
                        zIndex: '2',
                        textAlign: 'center'
                    } }, { children: [_jsx(Typography, Object.assign({ sx: { textAlign: 'center' } }, { children: t('Verifying') })), _jsx(CircularProgress, { sx: { display: 'inline-block' } })] })), _jsxs(Paper
                // Verification result
                , Object.assign({ 
                    // Verification result
                    sx: Object.assign(Object.assign({}, styles.paper), { display: stage !== EMAIL_LINK_STAGES.VERIFYING ? 'block' : 'none' }) }, { children: [_jsx(Box, Object.assign({ sx: styles.logo }, { children: _jsx("img", { src: studioGlobal, height: "40px" }) })), _jsx(Typography, Object.assign({ sx: styles.title }, { children: t(title) })), _jsx(Box
                        // Error notification
                        , Object.assign({ 
                            // Error notification
                            sx: {
                                width: '100%',
                                textAlign: 'center',
                                display: stage === EMAIL_LINK_STAGES.NOTIFY_ERROR ? 'inline-block' : 'none',
                                padding: '0 0 1rem',
                            } }, { children: _jsx(ErrorOutline, { sx: { color: 'red', width: '48px', height: '48px' } }) })), _jsx(Typography, Object.assign({ sx: styles.subtitle }, { children: t(subtitle) })), _jsx(Box
                        // Resend email block
                        , Object.assign({ 
                            // Resend email block
                            sx: { display: stage === EMAIL_LINK_STAGES.REINPUT_EMAIL ? 'block' : 'none' } }, { children: _jsx(EmailForm, { stage: stage, onSubmit: handleSubmit }) })), _jsx(Grid, Object.assign({ container: true, sx: {
                                width: '100%',
                                justifyContent: 'center',
                                display: stage === EMAIL_LINK_STAGES.NOTIFY_ERROR ? 'block' : 'none',
                            } }, { children: _jsx(Grid, Object.assign({ item: true }, { children: _jsx(ButtonSG
                                // Resend email button
                                , Object.assign({ 
                                    // Resend email button
                                    color: "secondary", variant: "contained", xstyles: styles.resendButton, onClick: () => {
                                        navigate(`${CLIENT_ENDPOINTS.SIGN_IN}`);
                                    }, startIcon: _jsx(MailOutline, { sx: { color: '#0A1F16', } }) }, { children: t('Resend') })) })) }))] }))] })) })));
};
